import { useDispatch, useSelector } from 'react-redux';
import { set, get, findIndex, flatMap, cloneDeep } from 'lodash';
import { useParams } from 'react-router-dom';
import { averageCostingFieldpath, quantitiesFieldpath, testingFieldpath, landedInfoFieldPath, additionalFieldPath, detailedCostingFieldpath } from '../fieldPath';
import { useProductCostingState } from '../ProductCostingContext';
import { createCostingDynamicGroupedArray, cloneData } from '../../../../Components/helper';
import useDetailedPageHook from './useDetailedPageHook';
import useDetailedCostingHooks from './useDetailedCostingHooks';
import { getProductCostingTemplateDetailsByIds } from '../store/productCosting.actions';
import useShowHideSectionHooks from './useShowHideSectionHooks';
import type { AdditionalState, DetailedCostingState, DetailedQuantitiesState, LandedInfoState, LandedInfoStateEntry, ProductCostingState, TestingState } from '../types';
import type { CostingTemplateDetail } from '../store/productCosting.types';

/** The redux slices this hook reads. */
interface RefreshRootState {
  newProductCostingData: {
    bomCompoList?: BomComponentLike[];
    productLengths?: NamedOption[];
    costingTemplateDetail?: CostingTemplateDetail | null;
  };
  SupplierSuggestion: { TestingLibraries?: TestingLibrary[] };
}

/** `{ _id, name }` option as carried by the colourway / size / length option arrays. */
interface NamedOption {
  _id?: string;
  name?: string;
  newReferences?: unknown;
  [key: string]: unknown;
}

/** A BOM component, as far as the refresh needs it. */
interface BomComponentLike {
  _id?: string;
  pricePerUnit?: unknown;
  quantity?: unknown;
  quantityUnitOfMeasure?: unknown;
  [key: string]: unknown;
}

interface TestingLibrary {
  _id?: string;
  testPrice?: string | number;
  [key: string]: unknown;
}

/** One colourway/size/length combination row produced by createCostingDynamicGroupedArray. */
interface ComboRow {
  colourway?: { _id?: string } | null;
  colourWay?: { _id?: string } | null;
  size?: string;
  length?: { _id?: string; name?: string } | null;
  landedInfo?: Array<{ _id?: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

/** A group of combination rows, keyed by its own expandData. */
interface ComboGroup {
  expandData?: ComboRow[];
  [key: string]: unknown;
}

/** One `{ key, value }` seed applied to every generated combination. */
interface ComboSeedKey {
  key: string;
  value: unknown;
}

/** The costing-template payload the refresh reads back after a toggle change. */
interface RefreshedTemplate extends CostingTemplateDetail {
  additionalInfo?: ComboRow[];
  landedInfoRowArr?: ComboRow[];
  landedInfo?: Array<{ _id?: string; [key: string]: unknown }>;
  findExcahngeRateCurrency?: unknown;
}

export interface UseRefreshHooksResult {
  refreshAllModules: (colourWayToggleCopy: boolean, sizeLengthToggleCopy: boolean) => Promise<void>;
  setRefreshCostingTEmplateTable: (
    colourWayToggleCopy: boolean,
    sizeLengthToggleCopy: boolean,
    dcIndex?: number,
  ) => Promise<LandedInfoStateEntry | LandedInfoState | undefined>;
}

/**
 * Rebuilds every costing module after the colourway / size+length breakdown toggles change, or
 * after the costing template is re-fetched.
 *
 * The common shape across modules: regenerate the full combination matrix from the current option
 * lists, then merge each existing row back in by a `colourway|size|length` key so user-entered
 * quantities and prices survive a combination change. Combinations that no longer exist are
 * dropped; new ones arrive seeded with defaults.
 */
export default function useRefreshHooks(): UseRefreshHooksResult {
  const {
    newProductCostingData: { bomCompoList, productLengths, costingTemplateDetail },
    SupplierSuggestion: { TestingLibraries },
  } = useSelector((reduxState: RefreshRootState) => reduxState);
  const { calculateTotPrice, calculatePricePerUnit } = useDetailedPageHook();
  const { devOrderId } = useParams<{ devOrderId?: string }>();

  const dispatch = useDispatch();
  const {
    productCostingstate,
    setProductCostingState,
    setInternalState,
    initialOptionsState,
    testingState,
    detailedCostingState,
    detailedQuanitiesState,
    landedInfoState,
    landedInfoStates,
    setLandedInfoStates,
    additionalState,
    setAdditionalState,
    setLandedInfoState,
  } = useProductCostingState();

  const { calculateAvrageCosting, calculateQuantities, handleSpecificationRequirementModule } = useDetailedPageHook();
  const { calculateDetailCosting } = useDetailedCostingHooks();
  const { hideLengthSysMaster } = useShowHideSectionHooks();

  const {
    averageCosting: { averageCostingActive },
  } = productCostingstate;

  const {
    detailedCosting: { detailedCostingActive },
  } = detailedCostingState;

  const { sizeOptionsArr, colourwayOptionsArr } = initialOptionsState;

  const refreshAllModules = async (colourWayToggleCopy: boolean, sizeLengthToggleCopy: boolean): Promise<void> => {
    let isCostingReset = false;
    if (averageCostingActive) {
      isCostingReset = await handleRefreshAverageCosting(colourWayToggleCopy, sizeLengthToggleCopy);
    }
    if (detailedCostingActive) {
      await handleRefreshQuantities(colourWayToggleCopy, sizeLengthToggleCopy);
      isCostingReset = await handleRefreshDetailedCosting(colourWayToggleCopy, sizeLengthToggleCopy);
    }
    if (isCostingReset) {
      await handleRefreshTesting();
    }
  };

  const setRefreshCostingTEmplateTable = async (
    colourWayToggleCopy: boolean,
    sizeLengthToggleCopy: boolean,
    dcIndex?: number,
  ): Promise<LandedInfoStateEntry | LandedInfoState | undefined> => {
    const res = (await dispatch(
      getProductCostingTemplateDetailsByIds(costingTemplateDetail?._id, devOrderId, {
        colourWayToggle: colourWayToggleCopy,
        sizeLengthToggle: sizeLengthToggleCopy,
      }) as never,
    )) as unknown as RefreshedTemplate | false;
    if (res) {
      await handleRefreshAdditonalModule(res);
      /* Re-seeds Spec Requirements from the refreshed template. This is the path that lets a costing
         created before BM-9899 pick the section up: the refresh rewrites the embedded snapshot, so
         the flag the card gates on finally arrives. */
      handleSpecificationRequirementModule(res as never);
      const newLandedInforState = await handleRefreshLandedInfoModule(res, dcIndex);
      setInternalState((prev) => ({
        ...prev,
        currSelectedTemplateDetails: res,
      }));
      return newLandedInforState;
    }
    return undefined;
  };

  const handleRefreshDetailedCosting = async (colourWayToggleCopy: boolean, sizeLengthToggleCopy: boolean): Promise<boolean> => {
    const copyState: DetailedCostingState = cloneDeep(detailedCostingState);
    const newBom = bomCompoList?.map((vl) => vl?._id);
    let currentDetailedCostingData: ComboGroup[] = cloneData(get(copyState, detailedCostingFieldpath().DETAILED_COSTING_DATA, []));
    currentDetailedCostingData = currentDetailedCostingData?.filter((vl) => newBom?.includes(vl?._id as string));
    const colourwayOptionsArrOpt: NamedOption[] = colourWayToggleCopy ? colourwayOptionsArr || [] : [];
    const sizeOptionsArrOpt: NamedOption[] = sizeLengthToggleCopy ? sizeOptionsArr || [] : [];
    const productLengthsOpt: NamedOption[] = hideLengthSysMaster && sizeLengthToggleCopy ? productLengths || [] : [];
    const setKeys: ComboSeedKey[] = [
      { key: 'quantityPerSizCol', value: '' },
      { key: 'quantityUnitOfMeasure', value: '0' },
      { key: 'pricePerUnit', value: '0' },
      {
        key: 'isExcluded',
        value: false,
      },
    ];

    const newCombination = (await createCostingDynamicGroupedArray(colourwayOptionsArrOpt, sizeOptionsArrOpt, productLengthsOpt, setKeys)) as ComboGroup[];
    for (const element of currentDetailedCostingData) {
      element.overAllQuantity = '0';
      element.overAllTotalPrice = '0';
      element.isExpand = false;
      element.totLength = [...new Set(productLengthsOpt.map((data) => data?.name))];
      element.totSize = [...new Set(sizeOptionsArrOpt.map((data) => data?.name))];
      element.totColourways = [...new Set(colourwayOptionsArrOpt.map((data) => data?.name))];
      element.totColourRefs = [...new Set(colourwayOptionsArrOpt.map((data) => data?.newReferences))];
      const getThenewObj = bomCompoList?.find((vl) => vl._id === element?._id);
      const existingMap = new Map<string, ComboRow>();
      element?.expandData?.forEach((combo) => {
        const key = getFlexibleComboKey(combo);
        existingMap.set(key, combo);
      });
      const cloneNewCombination: ComboRow[] = flatMap(newCombination?.map((val) => val?.expandData));
      const mergedExpandData = (cloneNewCombination || []).map((combo) => {
        const key = getFlexibleComboKey(combo);
        const old = existingMap.get(key);
        return {
          ...combo,
          quantityPerSizCol: old?.quantityPerSizCol ?? '0',
          pricePerUnit: getThenewObj?.pricePerUnit || old?.pricePerUnit || '0',
          totalPrice: old?.totalPrice ?? '0',
          quantity: getThenewObj?.quantity || old?.quantity || '0',
          quantityUnitOfMeasure: getThenewObj?.quantityUnitOfMeasure || old?.quantityUnitOfMeasure || null,
        };
      });
      element.expandData = mergedExpandData;
    }
    set(copyState, 'detailedCosting.bomTypeArr', currentDetailedCostingData);
    await calculateDetailCosting(copyState, undefined, false, productCostingstate?.productInfo?.currency);
    return true;
  };

  const handleRefreshTesting = async (): Promise<void> => {
    const copyState: TestingState = cloneDeep(testingState);
    const testingMap = new Map((TestingLibraries || []).map((test) => [test?._id, test]));
    const colourwayMap = new Map((colourwayOptionsArr || []).map((colour) => [colour?._id, colour]));

    const currentTestingData = (get(copyState, testingFieldpath().TESTING_INFO_DATA, []) as ComboRow[])
      ?.filter((item) => testingMap.has((item?.testingCode as { _id?: string } | undefined)?._id))
      ?.map((data) => ({
        ...data,
        colourway: colourwayMap?.get((data?.colourway as { _id?: string } | undefined)?._id) || null,
        totalPrice: testingMap?.get((data?.testingCode as { _id?: string } | undefined)?._id)?.testPrice || '0',
      }));

    if (currentTestingData?.length) {
      set(copyState, testingFieldpath().TESTING_INFO_DATA, currentTestingData);
      await calculateTotPrice(copyState, 'sumOfPrice');
      await calculatePricePerUnit(copyState);
    }
  };

  /** Identity for one combination row. Blank segments keep partially-configured rows matchable. */
  const getFlexibleComboKey = ({ colourway, size, length }: ComboRow): string => {
    return [colourway?._id || '', size || '', length?._id || ''].join('|');
  };

  const handleRefreshAverageCosting = async (colourWayToggleCopy: boolean, sizeLengthToggleCopy: boolean): Promise<boolean> => {
    const state: ProductCostingState = cloneDeep(productCostingstate);
    const currentAverageCosting: ComboGroup[] = cloneData(get(state, averageCostingFieldpath().AVERAGE_COSTING_INFO, []));

    const setKeys: ComboSeedKey[] = [
      { key: 'quantityPerSizCol', value: '' },
      { key: 'pricePerUnit', value: '' },
    ];

    const colourwayOptionsArrOpt: NamedOption[] = colourWayToggleCopy ? colourwayOptionsArr || [] : [];
    const sizeOptionsArrOpt: NamedOption[] = sizeLengthToggleCopy ? sizeOptionsArr || [] : [];
    const productLengthsOpt: NamedOption[] = hideLengthSysMaster && sizeLengthToggleCopy ? productLengths || [] : [];

    if (Array.isArray(currentAverageCosting) && currentAverageCosting.length > 0) {
      const newCombinationArr = (await createCostingDynamicGroupedArray(colourwayOptionsArrOpt, sizeOptionsArrOpt, productLengthsOpt, setKeys)) as ComboGroup[];
      const existingMap = new Map<string, ComboRow>();
      currentAverageCosting.forEach((group) => {
        (group.expandData || []).forEach((combo) => {
          const key = getFlexibleComboKey(combo);
          existingMap.set(key, combo);
        });
      });
      const mergedCombinations = newCombinationArr?.map((group) => {
        const mergedExpandData = (group.expandData || []).map((combo) => {
          const key = getFlexibleComboKey(combo);
          const old = existingMap.get(key);
          const oldMultiRows = old?.multiQuantityRows as unknown[] | undefined;
          return {
            ...combo,
            quantityPerSizCol: old?.quantityPerSizCol ?? '0',
            pricePerUnit: old?.pricePerUnit ?? '0',
            totalPrice: old?.totalPrice ?? '0',
            multiQuantityRows: oldMultiRows?.length ? oldMultiRows : [{ quantityPerSizCol: '', pricePerUnit: '', totalPrice: '' }],
            defaultRowIndex: old?.defaultRowIndex ?? 0,
          };
        });

        return {
          ...group,
          expandData: mergedExpandData,
        };
      });
      set(state, averageCostingFieldpath().AVERAGE_COSTING_INFO, mergedCombinations);
      await calculateAvrageCosting(state);
    }

    set(
      state,
      averageCostingFieldpath().TOTAL_AVERAGE_SIZES,
      sizeOptionsArrOpt?.map((data) => data.name),
    );
    set(
      state,
      averageCostingFieldpath().TOTAL_AVERAGE_LENGTH,
      productLengthsOpt?.map((data) => data.name),
    );
    set(
      state,
      averageCostingFieldpath().TOTAL_AVERAGE_COLOURWAYS,
      colourwayOptionsArrOpt?.map((data) => data.name),
    );

    setProductCostingState((prevState) => ({ ...prevState, ...state }));
    return true;
  };

  const handleRefreshAdditonalModule = async (costingTemplateNewChanges: RefreshedTemplate): Promise<void> => {
    const copyState: AdditionalState = cloneDeep(additionalState);
    const currAdditionalData: ComboRow[] = cloneData(get(copyState, additionalFieldPath().SELECTED_TEMP_ADDTIONAL_DATA, []));
    if (Array.isArray(costingTemplateNewChanges?.additionalInfo) && costingTemplateNewChanges?.additionalInfo.length > 0) {
      const refreshedAdditonalArray = currAdditionalData;
      const updatedRefreshAdditionalDAta: ComboRow[] = [];
      costingTemplateNewChanges?.additionalInfo.forEach((val) => {
        const foundIndex = findIndex(refreshedAdditonalArray, (add: ComboRow) => val._id === add._id);
        if (foundIndex >= 0) {
          const existObj = refreshedAdditonalArray[foundIndex] as ComboRow;
          const colourway = colourwayOptionsArr?.find((colour) => {
            return colour?._id === (existObj?.colourway as { _id?: string } | undefined)?._id;
          });
          existObj.colourway = (colourway as ComboRow['colourway']) || null;
          updatedRefreshAdditionalDAta.push(existObj);
        }
        if (foundIndex < 0) {
          updatedRefreshAdditionalDAta.push(val);
        }
      });
      set(copyState, additionalFieldPath().SELECTED_TEMP_ADDTIONAL_DATA, updatedRefreshAdditionalDAta);
    } else {
      set(copyState, 'additional', {
        additionalInfo: [
          {
            colourway: null,
            quantityUom: null,
            totalQuantityUom: null,
            quantityPerUnit: '',
            pricePerUnit: '',
            title: '',
            totalQuantity: '',
            pricePerUoM: '',
            quantityPerColSizLen: '',
            totalPrice: '',
            isPricePerUnitDisabled: false,
            isTotalPriceDisabled: false,
          },
        ],
      });
    }
    setAdditionalState((prevState) => ({ ...prevState, ...copyState }));
  };

  const handleRefreshLandedInfoModule = async (
    costingTemplateNewChanges: RefreshedTemplate,
    dcIndex?: number,
  ): Promise<LandedInfoStateEntry | LandedInfoState> => {
    if (typeof dcIndex === 'number' && dcIndex >= 0 && landedInfoStates[dcIndex]) {
      const copyState: LandedInfoStateEntry = cloneDeep(landedInfoStates[dcIndex]);
      const currLandedInfoData: ComboRow[] = cloneData(get(copyState, landedInfoFieldPath().LANDED_INFO_DATA, []));
      if (Array.isArray(currLandedInfoData) && currLandedInfoData.length) {
        const newupdatedLandedInfoRowArr = syncData(currLandedInfoData, costingTemplateNewChanges.landedInfoRowArr || [], costingTemplateNewChanges.landedInfo);
        set(copyState, landedInfoFieldPath().LANDEDINFO_CURRENCY_ARR, costingTemplateNewChanges?.findExcahngeRateCurrency);
        set(copyState, landedInfoFieldPath().LANDED_INFO_DATA, newupdatedLandedInfoRowArr);
        setProductCostingState((prevState) => ({ ...prevState, landedInformationColumns: costingTemplateNewChanges?.landedInfo as unknown[] }));
        setLandedInfoStates((prev) => {
          const copy = [...prev];
          copy[dcIndex] = { ...(copy[dcIndex] as LandedInfoStateEntry), ...copyState };
          return copy;
        });
      }
      return copyState;
    }

    const copyState: LandedInfoState = cloneDeep(landedInfoState);
    const currLandedInfoData: ComboRow[] = cloneData(get(copyState, landedInfoFieldPath().LANDED_INFO_DATA, []));
    if (Array.isArray(currLandedInfoData) && currLandedInfoData.length) {
      const newupdatedLandedInfoRowArr = syncData(currLandedInfoData, costingTemplateNewChanges.landedInfoRowArr || [], costingTemplateNewChanges.landedInfo);
      set(copyState, landedInfoFieldPath().LANDEDINFO_CURRENCY_ARR, costingTemplateNewChanges?.findExcahngeRateCurrency);
      set(copyState, landedInfoFieldPath().LANDED_INFO_DATA, newupdatedLandedInfoRowArr);
      setProductCostingState((prevState) => ({ ...prevState, landedInformationColumns: costingTemplateNewChanges?.landedInfo as unknown[] }));
      setLandedInfoState((prevState) => ({ ...prevState, ...copyState }));
    }
    return copyState;
  };

  /**
   * Merges the template's fresh landed rows over the saved ones. A saved row is kept when its
   * colourway/size/length key still exists; within a kept row, landed columns the template dropped
   * are removed and columns it added are appended.
   */
  function syncData(oldArr: ComboRow[], newArr: ComboRow[], landedInfo: RefreshedTemplate['landedInfo']): ComboRow[] {
    const buildKey = (obj: ComboRow): string => `${obj?.colourway?._id || obj?.colourWay?._id || ''}-${obj?.size || ''}-${obj?.length?._id || ''}`;

    const oldMap = new Map(oldArr.map((item) => [buildKey(item), item]));

    const final: ComboRow[] = [];

    for (const newItem of newArr) {
      const key = buildKey(newItem);

      if (oldMap.has(key)) {
        const oldItem = oldMap.get(key) as ComboRow;

        const oldLandedMap = new Map((oldItem.landedInfo || []).map((li) => [li._id, li]));
        const newLandedMap = new Map((landedInfo || []).map((li) => [li._id, li]));

        const finalLandedInfo: Array<{ _id?: string; [key: string]: unknown }> = [];

        for (const [id, oldLI] of oldLandedMap.entries()) {
          if (newLandedMap.has(id)) {
            finalLandedInfo.push(oldLI);
          }
        }

        for (const [id, newLI] of newLandedMap.entries()) {
          if (!oldLandedMap.has(id)) {
            finalLandedInfo.push(newLI);
          }
        }

        oldItem.landedInfo = finalLandedInfo;
        final.push(oldItem);
      } else {
        final.push(newItem);
      }
    }
    return final;
  }

  const handleRefreshQuantities = async (colourWayToggleCopy: boolean, sizeLengthToggleCopy: boolean): Promise<void> => {
    const copyState: DetailedQuantitiesState = cloneDeep(detailedQuanitiesState);
    const currentQuantities: ComboGroup[] = cloneData(get(copyState, quantitiesFieldpath().QUANTITES_INFO, []));

    const setKeys: ComboSeedKey[] = [{ key: 'quantityPerSizCol', value: '' }];
    const colourwayOptionsArrOpt: NamedOption[] = colourWayToggleCopy ? colourwayOptionsArr || [] : [];
    const sizeOptionsArrOpt: NamedOption[] = sizeLengthToggleCopy ? sizeOptionsArr || [] : [];
    const productLengthsOpt: NamedOption[] = hideLengthSysMaster && sizeLengthToggleCopy ? productLengths || [] : [];

    if (Array.isArray(currentQuantities) && currentQuantities.length > 0) {
      const newCombinationArr = (await createCostingDynamicGroupedArray(colourwayOptionsArrOpt, sizeOptionsArrOpt, productLengthsOpt, setKeys)) as ComboGroup[];
      const existingMap = new Map<string, ComboRow>();
      currentQuantities.forEach((group) => {
        (group.expandData || []).forEach((combo) => {
          const key = getFlexibleComboKey(combo);
          existingMap.set(key, combo);
        });
      });
      const mergedCombinations = newCombinationArr?.map((group) => {
        const mergedExpandData = (group.expandData || []).map((combo) => {
          const key = getFlexibleComboKey(combo);
          const old = existingMap.get(key);
          return {
            ...combo,
            quantityPerSizCol: old?.quantityPerSizCol ?? '0',
          };
        });

        return {
          ...group,
          expandData: mergedExpandData,
        };
      });
      set(copyState, quantitiesFieldpath().QUANTITES_INFO, mergedCombinations);
    }
    set(
      copyState,
      quantitiesFieldpath().TOTAL_AVERAGE_SIZES,
      sizeOptionsArrOpt.map((data) => data.name),
    );
    set(
      copyState,
      quantitiesFieldpath().TOTAL_AVERAGE_LENGTH,
      productLengthsOpt.map((data) => data.name),
    );
    set(
      copyState,
      quantitiesFieldpath().TOTAL_AVERAGE_COLOURWAYS,
      colourwayOptionsArrOpt.map((data) => data.name),
    );
    await calculateQuantities(copyState);
  };

  return { refreshAllModules, setRefreshCostingTEmplateTable };
}
